import type { ReactNode } from "react"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { EditorialHero } from "./editorial-hero"
import { FAQAccordion } from "./faq-accordion"
import { RelatedToolsRail, type RelatedToolItem } from "./related-tools-rail"
import { CTABand } from "./cta-band"
import { SectionRule } from "./section-rule"
import { ToolProductCarousel } from "./tool-product-carousel"
import { ToolRecentlyViewedRail } from "./tool-recently-viewed-rail"
import { BreadcrumbJsonLd } from "@/components/seo/breadcrumb-jsonld"
import { toolBreadcrumbs, type BreadcrumbTrail } from "@/lib/breadcrumbs"
import type { ToolCarouselRequest } from "@/lib/tool-carousel-key"

interface FAQItem {
  question: string
  answer: string
}

type TopicFamily =
  | "fridge"
  | "freezer"
  | "wine"
  | "beverage"
  | "ice"
  | "mini"
  | "rv"
  | "outdoor"
  | "commercial"
  | "filter"
  | "kitchen-helper"

interface BonusRail {
  eyebrow: string
  title: string
  description: string
  collectionType: string
  fallbackCollectionType?: string
  /** FRIDGE-1620: a title that names a feature reads only the rows whose title names it. */
  titleMatch?: string[]
}

interface BonusCarouselConfig {
  primary: BonusRail
  secondary: BonusRail
}

/**
 * Topic-family → bonus-carousel pair. The shell renders these between
 * FAQ → RelatedTools and between RelatedTools → CTA on every tool page.
 * Derived from toolName via deriveFamily(), so every one of the 90 tools gets
 * its pair without per-page wiring.
 */
// NOTE: Three Supabase collections are unpopulated as of 2026-04-27:
//   beverage-center, undercounter-refrigerator, energy-star
// The map below avoids those collections entirely. Update once they're filled.
// FRIDGE-1501 F7: the French door rail said "Trending this week", "Most-shopped
// refrigerators" and "Regularly updated bestsellers"; Fridge.com has no sales or
// trend data, so it now says what it lists. The rails are random picks from a
// collection type, so no title or eyebrow ranks them either: the release check of
// 10/15/2026 found "Best-selling upright freezers", "Top-rated wine refrigerators",
// "the best built-in ice makers", "the best built-in water filtration", "Best value"
// and "strong efficiency ratings" over random picks, and an NSF/ANSI claim no row backs.
// FRIDGE-1532 and FRIDGE-1620, on the pull request 812 preview (10/17/2026, the rails as
// a browser renders them): these shared rails still said "the highest-volume layout",
// promised features nobody checked ("with through-door dispensers" and "with built-in
// ice" over any side-by-side or bottom-freezer, "Outdoor & garage-rated", "better
// seal-test ratings", "compact" over full-size top-freezers, "commercial picks", "sized
// for serious collections") and said each product page lists its energy use, whether it
// makes ice and its water filter, which a product page shows only when its row holds it
// (1 of 5 counter-depth rows has energy use, 201 of 728 French door rows an ice maker).
// The live catalog backs two features (active, eligible rows whose title names it,
// 10/17/2026): a dispenser on 37 side-by-sides, an ice maker on 23 bottom-freezers, so
// those rails read only those rows; the rest name their type and give a type fact or advice.
const BONUS_CAROUSELS: Record<TopicFamily, BonusCarouselConfig> = {
  fridge: {
    primary: { eyebrow: "French door", title: "French door refrigerators to compare", description: "French door models from our catalog: two fresh-food doors above a pull-out freezer.", collectionType: "french-door", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth", title: "Counter-depth refrigerators to compare", description: "Counter-depth models from our catalog: shallower than a standard fridge, so the doors sit closer to the counter edge.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
  },
  freezer: {
    primary: { eyebrow: "Upright", title: "Upright freezers to compare", description: "Upright models from our catalog: the easiest layout for daily-access frozen storage.", collectionType: "upright-freezer", fallbackCollectionType: "bottom-freezer" },
    secondary: { eyebrow: "Bulk storage", title: "Chest freezers built for the long haul", description: "Lowest cost per cubic foot and steadiest temperatures for long-term storage.", collectionType: "chest-freezer", fallbackCollectionType: "upright-freezer" },
  },
  wine: {
    primary: { eyebrow: "For the cellar", title: "Wine refrigerators to compare", description: "Wine coolers from our catalog. Count your bottles, then compare each cooler's capacity and zones.", collectionType: "wine-cooler", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "If wine alone won't cut it", title: "Side-by-side fridges with through-door dispensers", description: "When the household needs a full fridge with water at the door, alongside the wine.", collectionType: "side-by-side", fallbackCollectionType: "french-door", titleMatch: ["dispenser"] },
  },
  beverage: {
    primary: { eyebrow: "Counter-depth", title: "Counter-depth fridges for entertaining", description: "Counter-depth models sit closer to flush with the counters, which keeps a bar or party space open.", collectionType: "counter-depth", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Side-by-side", title: "Side-by-side fridges with through-door dispensers", description: "Two narrow doors and water at the door, so guests can fill a glass without opening the fridge.", collectionType: "side-by-side", fallbackCollectionType: "french-door", titleMatch: ["dispenser"] },
  },
  ice: {
    primary: { eyebrow: "French door", title: "French door refrigerators to compare", description: "French door models from our catalog. If you need ice, check each listing for an ice maker.", collectionType: "french-door", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Bottom-freezer pick", title: "Bottom-freezer fridges with built-in ice makers", description: "Bottom-freezer models whose listing names an ice maker, with fresh food at eye level.", collectionType: "bottom-freezer", fallbackCollectionType: "french-door", titleMatch: ["ice maker", "icemaker"] },
  },
  mini: {
    primary: { eyebrow: "Top-freezer", title: "Top-freezer refrigerators to compare", description: "The simplest layout, with the freezer on top. Measure the space before you pick a size.", collectionType: "top-freezer", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth", title: "Counter-depth fridges that fit tight spaces", description: "When you need full features but every inch of clearance counts.", collectionType: "counter-depth", fallbackCollectionType: "bottom-freezer" },
  },
  rv: {
    primary: { eyebrow: "Counter-depth", title: "Counter-depth refrigerators to compare", description: "A fridge on the road needs a rating for travel and for the rig's power; check the manual before you install a home model.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
    secondary: { eyebrow: "Top-freezer", title: "Top-freezer refrigerators to compare", description: "The simplest mechanical layout — fewer points of failure when you're miles from a tech.", collectionType: "top-freezer", fallbackCollectionType: "bottom-freezer" },
  },
  outdoor: {
    primary: { eyebrow: "Bottom-freezer", title: "Bottom-freezer refrigerators to compare", description: "A garage or outdoor fridge must be rated for that space's temperatures; the manual lists the range it can run in.", collectionType: "bottom-freezer", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth fit", title: "Counter-depth picks for tight spaces", description: "Shallower than a standard fridge. For an outdoor kitchen, choose a model rated for outdoor use.", collectionType: "counter-depth", fallbackCollectionType: "french-door" },
  },
  commercial: {
    primary: { eyebrow: "Side-by-side", title: "Side-by-side refrigerators to compare", description: "Two narrow doors mean shorter open cycles — better for shared kitchens.", collectionType: "side-by-side", fallbackCollectionType: "french-door" },
    secondary: { eyebrow: "Bottom-freezer", title: "Bottom-freezer refrigerators to compare", description: "Lower-bay frozen storage with full-width fresh access on top.", collectionType: "bottom-freezer", fallbackCollectionType: "side-by-side" },
  },
  filter: {
    primary: { eyebrow: "French door", title: "French door refrigerators to compare", description: "French door models from our catalog. Note the model number: it is how you find the water filter that fits.", collectionType: "french-door", fallbackCollectionType: "side-by-side" },
    secondary: { eyebrow: "Side-by-side", title: "Side-by-side fridges with through-door dispensers", description: "Two narrow doors and a dispenser in the door. When the water slows or tastes off, the filter is due.", collectionType: "side-by-side", fallbackCollectionType: "counter-depth", titleMatch: ["dispenser"] },
  },
  "kitchen-helper": {
    primary: { eyebrow: "French door", title: "French door refrigerators to compare", description: "French door models from our catalog: two fresh-food doors above a pull-out freezer.", collectionType: "french-door", fallbackCollectionType: "counter-depth" },
    secondary: { eyebrow: "Counter-depth", title: "Counter-depth refrigerators to compare", description: "Counter-depth models from our catalog: shallower than a standard fridge, so the doors sit closer to the counter edge.", collectionType: "counter-depth", fallbackCollectionType: "top-freezer" },
  },
}

/**
 * Derive a topic family from the tool's name. Falls back to "fridge" —
 * the safest universal default. Designed to be wrong about ~5% of edge
 * cases rather than require per-page wiring on all 90 tools.
 */
function deriveFamily(toolName?: string): TopicFamily {
  if (!toolName) return "fridge"
  const n = toolName.toLowerCase()
  if (/\bwine\b/.test(n)) return "wine"
  if (/\bkegerator\b|\bbeer\b|\bbeverage\b/.test(n)) return "beverage"
  if (/\bice\s*maker\b/.test(n)) return "ice"
  if (/\bmini\s*fridge\b|\bdorm\b/.test(n)) return "mini"
  if (/\brv\b/.test(n)) return "rv"
  if (/\boutdoor\b|\bgarage\b/.test(n)) return "outdoor"
  if (/\bwalk-?in\b|\bcommercial\b|\bdisplay\s*cooler\b/.test(n)) return "commercial"
  if (/\bfilter\b/.test(n)) return "filter"
  if (/\bcooking\b|\brecipe\b|\bkitchen\s*timer\b|\btemperature\s*converter\b/.test(n)) return "kitchen-helper"
  if (/\bfreezer\b/.test(n) && !/\brefrigerator\b/.test(n)) return "freezer"
  return "fridge"
}

/** FRIDGE-1501 (F2): the shell's two bonus carousel reads, so a server page
 *  can load them ahead of time (lib/tool-carousel-prefetch.ts). */
export function toolPageBonusCarousels(toolName?: string, topicFamily?: TopicFamily): ToolCarouselRequest[] {
  const bonus = BONUS_CAROUSELS[topicFamily ?? deriveFamily(toolName)]
  return [bonus.primary, bonus.secondary].map((c) => ({
    collectionType: c.collectionType,
    fallbackCollectionType: c.fallbackCollectionType,
    randomize: true,
    titleMatch: c.titleMatch,
  }))
}

export interface ToolPageShellProps {
  /**
   * The canonical tool name — the page H1 (the editorial display text is
   * demoted to H2) AND the breadcrumb leaf: the shell builds the page's ONE
   * trail, `Fridge.com / Tools / {toolName}`, with lib/breadcrumbs.ts
   * toolBreadcrumbs() and renders it as BOTH the visible hero breadcrumb and
   * the BreadcrumbList JSON-LD (FRIDGE-269). Every /tools page passes it.
   */
  toolName?: string
  /**
   * An explicit trail from lib/breadcrumbs.ts for a shell page that is NOT a
   * tool (e.g. /widgets → staticPageBreadcrumbs). Wins over the toolName-
   * derived trail; rendered on both surfaces the same way.
   */
  trail?: BreadcrumbTrail
  /** Hero orange eyebrow (e.g. "Decision tool · Specialty") */
  heroEyebrow: string
  /** Hero H1 — pre-italic text */
  heroTitlePre: ReactNode
  /** Hero H1 — italic orange accent word */
  heroTitleItalic: ReactNode
  /** Hero H1 — optional text after the italic */
  heroTitlePost?: ReactNode
  /** Hero subtitle */
  heroSubtitle?: ReactNode
  /** Hero meta row strings */
  heroMeta?: string[]
  /** Optional share bar rendered just under the hero meta row */
  heroShare?: ReactNode
  /** Optional hero right-slot (companion card / panel) */
  heroRight?: ReactNode

  /** The main variant body — calculator, quiz, comparison, guide, etc. */
  children: ReactNode

  /** FAQ items rendered after the body */
  faqs?: FAQItem[]
  /** Optional FAQ section eyebrow (default: "Questions") */
  faqEyebrow?: string
  /** Optional FAQ section title (default: "Frequently asked") */
  faqTitle?: string

  /** Related tools rendered below the FAQ */
  relatedTools?: RelatedToolItem[]

  /**
   * Optional explicit override for the topic family used to pick the two
   * bonus carousels (rendered between FAQ→Related and Related→CTA). If
   * omitted, the family is inferred from `toolName`. Set this when the
   * tool name doesn't obviously match its actual product family
   * (e.g., a "Carbon Footprint Calculator" that's really about freezers).
   */
  topicFamily?: TopicFamily

  /** CTA band rendered at the bottom of the page */
  cta?: {
    eyebrow?: string
    title: string
    body: string
    buttonLabel: string
    buttonHref: string
  }
}

/**
 * ToolPageShell — the standard wrapper for every redesigned tool page.
 * Composes the editorial chrome (SiteHeader · EditorialHero · main · FAQ
 * section · RelatedToolsRail · CTABand · SiteFooter) around a single
 * variant-specific body slot.
 *
 * This is the leaner successor to `ToolPageTemplate` for tool pages that
 * follow the /tools redesign language. Individual variant pages (A–E) all
 * sit inside this shell and render their variant-specific sections as
 * `children`.
 */
export function ToolPageShell({
  toolName,
  trail: explicitTrail,
  heroEyebrow,
  heroTitlePre,
  heroTitleItalic,
  heroTitlePost,
  heroSubtitle,
  heroMeta,
  heroShare,
  heroRight,
  children,
  faqs,
  faqEyebrow = "Questions",
  faqTitle = "Frequently asked",
  relatedTools,
  topicFamily,
  cta,
}: ToolPageShellProps) {
  const bonus = BONUS_CAROUSELS[topicFamily ?? deriveFamily(toolName)]
  // ONE trail — Fridge.com / Tools / {toolName} (or the explicit trail a
  // non-tool shell page passes) — rendered visibly in the hero and emitted as
  // the BreadcrumbList below from this same array. (Until FRIDGE-269 each
  // page passed a shortened visible leaf such as "Age Calculator" while the
  // layout-level JSON-LD said "Free Tools / Refrigerator Age Calculator".)
  const trail: BreadcrumbTrail = explicitTrail ?? (toolName ? toolBreadcrumbs({ name: toolName }) : [])
  return (
    <>
      <BreadcrumbJsonLd trail={trail} id="tool-breadcrumb-jsonld" />
      <SiteHeader />

      {/* FRIDGE-1508 K1508-2: the skip link's #main-content target, once for
          every page in this shell (89 tools and /widgets). Before it the shell
          rendered no main landmark, so on every tool page the skip link went
          nowhere and the next Tab landed on the header logo. Pages inside the
          shell must not open a second main element of their own. */}
      <main id="main-content">
        <EditorialHero
          trail={trail}
          toolName={toolName}
          eyebrow={heroEyebrow}
          titlePre={heroTitlePre}
          titleItalic={heroTitleItalic}
          titlePost={heroTitlePost}
          subtitle={heroSubtitle}
          meta={heroMeta}
          share={heroShare}
          right={heroRight}
        />

        {children}

        {faqs && faqs.length > 0 && (
          <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
            <div className="max-w-[1120px] mx-auto px-6 md:px-8">
              <SectionRule eyebrow={faqEyebrow} title={faqTitle} />
              <FAQAccordion
                items={faqs}
                columns={1}
                defaultOpen={[0]}
                title=""
                bordered={false}
              />
            </div>
          </section>
        )}

        {/* Bonus carousel #1 — placed after FAQ so it's not adjacent to the
            variant body's own carousels. Topic-family-derived from toolName.
            randomize=true so we don't render identical products to a body
            carousel that may use the same collection. */}
        <section className="bg-white py-4 md:py-6">
          <ToolProductCarousel
            eyebrow={bonus.primary.eyebrow}
            title={bonus.primary.title}
            description={bonus.primary.description}
            collectionType={bonus.primary.collectionType}
            fallbackCollectionType={bonus.primary.fallbackCollectionType}
            titleMatch={bonus.primary.titleMatch}
            randomize
          />
        </section>

        {relatedTools && relatedTools.length > 0 && <RelatedToolsRail tools={relatedTools} />}

        {/* Bonus carousel #2 — between RelatedTools and CTA. */}
        <section className="bg-white py-4 md:py-6">
          <ToolProductCarousel
            eyebrow={bonus.secondary.eyebrow}
            title={bonus.secondary.title}
            description={bonus.secondary.description}
            collectionType={bonus.secondary.collectionType}
            fallbackCollectionType={bonus.secondary.fallbackCollectionType}
            titleMatch={bonus.secondary.titleMatch}
            randomize
          />
        </section>

        {cta && (
          <CTABand
            eyebrow={cta.eyebrow}
            title={cta.title}
            body={cta.body}
            buttonLabel={cta.buttonLabel}
            buttonHref={cta.buttonHref}
          />
        )}
      </main>

      <ToolRecentlyViewedRail />

      <SiteFooter />
    </>
  )
}
